/**
 * Invoice Actions Component
 * Action buttons for invoice operations
 * AC-4: Action Buttons
 */

import { Button } from '@/components/ui/button';
import { InvoiceStatus } from '@/types';
import {
  Download,
  Mail,
  DollarSign,
  FileText,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface InvoiceActionsProps {
  invoiceId: number;
  invoiceStatus: InvoiceStatus;
  orderId?: number;
  onDownloadPdf: () => void;
  onSendEmail: () => void;
  onRecordPayment: () => void;
  onViewOrder: () => void;
  isDownloading?: boolean;
  isSendingEmail?: boolean;
}

export const InvoiceActions = ({
  invoiceStatus,
  orderId,
  onDownloadPdf,
  onSendEmail,
  onRecordPayment,
  onViewOrder,
  isDownloading = false,
  isSendingEmail = false,
}: InvoiceActionsProps) => {
  // Determine button states based on invoice status
  const canRecordPayment =
    invoiceStatus === InvoiceStatus.ISSUED ||
    invoiceStatus === InvoiceStatus.PARTIALLY_PAID ||
    invoiceStatus === InvoiceStatus.OVERDUE;

  const canSendEmail = invoiceStatus !== InvoiceStatus.CANCELLED && invoiceStatus !== InvoiceStatus.DRAFT;
  const canDownload = invoiceStatus !== InvoiceStatus.DRAFT;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-wrap gap-3">
          {/* Download PDF */}
          <Button
            onClick={onDownloadPdf}
            disabled={!canDownload || isDownloading}
            variant="outline"
            className="flex-1 sm:flex-none"
          >
            {isDownloading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </>
            )}
          </Button>

          {/* Send Email */}
          <Button
            onClick={onSendEmail}
            disabled={!canSendEmail || isSendingEmail}
            variant="outline"
            className="flex-1 sm:flex-none"
          >
            {isSendingEmail ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Send Email
              </>
            )}
          </Button>

          {/* Record Payment */}
          <Button
            onClick={onRecordPayment}
            disabled={!canRecordPayment}
            variant="default"
            className="flex-1 sm:flex-none"
          >
            <DollarSign className="mr-2 h-4 w-4" />
            Record Payment
          </Button>

          {/* View Order */}
          {orderId && (
            <Button
              onClick={onViewOrder}
              variant="outline"
              className="flex-1 sm:flex-none"
            >
              <FileText className="mr-2 h-4 w-4" />
              View Order
            </Button>
          )}
        </div>

        {/* Help text for disabled buttons */}
        <div className="mt-3 text-sm text-muted-foreground space-y-1">
          {!canRecordPayment && (
            <p>
              {invoiceStatus === InvoiceStatus.PAID
                ? 'Invoice is already paid in full'
                : invoiceStatus === InvoiceStatus.CANCELLED
                ? 'Cannot record payment for cancelled invoice'
                : 'Invoice must be issued before recording payments'}
            </p>
          )}
          {!canDownload && (
            <p>Invoice must be issued before downloading</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
